import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getDevices, createDevice, updateDevice } from '../api/endpoints';
import type { Device } from '../types';
import { formatRelative } from '../utils/dates';

const BRAND = '#00955F';
const BRAND_50 = '#E0F1E8';

export default function AdminPage() {
  return (
    <div className="flex flex-col h-full page-enter" style={{ background: 'var(--color-bg-base)' }}>
      {/* Page header */}
      <div style={{ background: '#fff', borderBottom: '1px solid var(--color-border)', padding: '20px 24px 16px' }}>
        <div className="page-header" style={{ marginBottom: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div className="icon-badge">
              <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
            </div>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--color-text)', letterSpacing: '-0.02em', margin: 0, lineHeight: 1.2 }}>
                Administração
              </h1>
              <p style={{ fontSize: 12, color: 'var(--color-text-dim)', margin: '4px 0 0' }}>
                Gerencie dispositivos e configurações
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto scrollbar-thin" style={{ padding: 24 }}>
        <DevicesTab />
      </div>
    </div>
  );
}

function DevicesTab() {
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newImei, setNewImei] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newSms, setNewSms] = useState('');

  const { data: devices = [] } = useQuery({
    queryKey: ['devices'],
    queryFn: getDevices,
  });

  const createMutation = useMutation({
    mutationFn: createDevice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      toast.success('Dispositivo adicionado.');
      setShowAddForm(false);
      setNewImei('');
      setNewLabel('');
      setNewSms('');
    },
    onError: () => toast.error('Falha ao adicionar dispositivo.'),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      updateDevice(id, { enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
    onError: () => toast.error('Falha ao atualizar dispositivo.'),
  });

  function handleAddDevice(e: React.FormEvent) {
    e.preventDefault();
    createMutation.mutate({
      imei: newImei.trim(),
      label: newLabel.trim(),
      smsNumber: newSms.trim() || null,
    });
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', height: 38, padding: '0 12px',
    border: '1px solid var(--color-border-strong)', borderRadius: 10,
    fontSize: 13, background: 'var(--color-bg-base)', color: 'var(--color-text)',
    outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 13, color: 'var(--color-text-muted)', fontWeight: 600 }}>
          {devices.length} dispositivo{devices.length !== 1 ? 's' : ''}
        </span>
        <button
          onClick={() => setShowAddForm((v) => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 16px', background: BRAND, color: '#fff',
            fontSize: 13, fontWeight: 700, borderRadius: 10, border: 'none',
            boxShadow: 'var(--shadow-brand)',
          }}
        >
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
            <path d="M12 4v16m8-8H4" />
          </svg>
          Adicionar dispositivo
        </button>
      </div>

      {/* Add form */}
      {showAddForm && (
        <form
          onSubmit={handleAddDevice}
          style={{
            background: '#fff', borderRadius: 16, padding: 20, marginBottom: 16,
            border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-sm)',
          }}
        >
          <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-text)', marginBottom: 14, marginTop: 0 }}>
            Novo dispositivo
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>
                IMEI (15 dígitos)
              </label>
              <input
                style={inputStyle}
                type="text" value={newImei} onChange={(e) => setNewImei(e.target.value)}
                required pattern="\d{15}" maxLength={15} placeholder="359586015829802"
                onFocus={(e) => { e.currentTarget.style.borderColor = BRAND; e.currentTarget.style.boxShadow = `0 0 0 3px ${BRAND}22`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-strong)'; e.currentTarget.style.boxShadow = 'none'; }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>
                Nome / Identificação
              </label>
              <input
                style={inputStyle}
                type="text" value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
                required placeholder="Carro da empresa"
                onFocus={(e) => { e.currentTarget.style.borderColor = BRAND; e.currentTarget.style.boxShadow = `0 0 0 3px ${BRAND}22`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-strong)'; e.currentTarget.style.boxShadow = 'none'; }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>
                Número SMS (opcional)
              </label>
              <input
                style={inputStyle}
                type="tel" value={newSms} onChange={(e) => setNewSms(e.target.value)}
                placeholder="+5511999999999"
                onFocus={(e) => { e.currentTarget.style.borderColor = BRAND; e.currentTarget.style.boxShadow = `0 0 0 3px ${BRAND}22`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-strong)'; e.currentTarget.style.boxShadow = 'none'; }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="submit"
              disabled={createMutation.isPending}
              style={{
                padding: '8px 18px', background: BRAND, color: '#fff',
                fontSize: 13, fontWeight: 700, borderRadius: 10, border: 'none',
                opacity: createMutation.isPending ? 0.6 : 1,
              }}
            >
              {createMutation.isPending ? 'Salvando...' : 'Salvar'}
            </button>
            <button
              type="button"
              onClick={() => setShowAddForm(false)}
              style={{
                padding: '8px 18px', background: 'var(--color-bg-sunken)', color: 'var(--color-text-muted)',
                fontSize: 13, fontWeight: 600, borderRadius: 10, border: 'none',
              }}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* Table card */}
      <div style={{
        background: '#fff', borderRadius: 16, overflow: 'hidden',
        border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-sm)',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              {['IMEI', 'Nome', 'Último contato', 'Número SMS', 'Status', 'Ativo'].map((h) => (
                <th key={h} style={{
                  padding: '13px 20px', textAlign: 'left',
                  fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: '0.14em', color: 'var(--color-text-dim)',
                  background: 'var(--color-bg-base)',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {devices.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-dim)', fontSize: 13 }}>
                  Nenhum dispositivo cadastrado.
                </td>
              </tr>
            )}
            {devices.map((device) => (
              <DeviceRow
                key={device.id}
                device={device}
                onToggle={(enabled) => toggleMutation.mutate({ id: device.id, enabled })}
                isToggling={toggleMutation.isPending && (toggleMutation.variables as { id: number })?.id === device.id}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface DeviceRowProps {
  device: Device;
  onToggle: (enabled: boolean) => void;
  isToggling: boolean;
}

function DeviceRow({ device, onToggle, isToggling }: DeviceRowProps) {
  const isOnline = device.lastSeenAt
    ? Date.now() - new Date(device.lastSeenAt).getTime() < 10 * 60 * 1000
    : false;

  const statusColor = isOnline ? BRAND : '#D32020';
  const statusBg = isOnline ? BRAND_50 : '#FADEDE';

  return (
    <tr
      style={{ borderBottom: '1px solid var(--color-border)' }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = 'var(--color-bg-base)'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = ''; }}
    >
      <td style={{ padding: '13px 20px', fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--color-text-muted)' }}>
        {device.imei}
      </td>
      <td style={{ padding: '13px 20px', fontWeight: 700, color: 'var(--color-text)' }}>
        {device.label || '—'}
      </td>
      <td style={{ padding: '13px 20px', fontSize: 12, color: 'var(--color-text-muted)' }}>
        {device.lastSeenAt ? formatRelative(device.lastSeenAt) : '—'}
      </td>
      <td style={{ padding: '13px 20px', fontSize: 12, color: 'var(--color-text-muted)', fontFamily: 'var(--font-mono)' }}>
        {device.smsNumber ?? '—'}
      </td>
      <td style={{ padding: '13px 20px' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '3px 10px', borderRadius: 9999,
          fontSize: 11.5, fontWeight: 700,
          background: statusBg, color: statusColor,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusColor }} />
          {isOnline ? 'Online' : 'Offline'}
        </span>
      </td>
      <td style={{ padding: '13px 20px' }}>
        <button
          role="switch"
          aria-checked={device.enabled}
          onClick={() => onToggle(!device.enabled)}
          disabled={isToggling}
          style={{
            position: 'relative', display: 'inline-flex', height: 22, width: 40,
            alignItems: 'center', borderRadius: 9999,
            background: device.enabled ? BRAND : '#C4C1BA',
            border: 'none', transition: 'background 0.2s', opacity: isToggling ? 0.5 : 1,
          }}
        >
          <span style={{
            display: 'inline-block', width: 16, height: 16, borderRadius: '50%',
            background: '#fff', boxShadow: 'var(--shadow-sm)',
            transform: device.enabled ? 'translateX(20px)' : 'translateX(3px)',
            transition: 'transform 0.2s',
          }} />
        </button>
      </td>
    </tr>
  );
}
